import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
interface AnimatedCounterProps {
    end: number;
    duration?: number;
    prefix?: string;
    suffix?: string;
    className?: string;
}
function toPersianDigits(input: string | number) {
    const map: Record<string, string> = {
        '0': '۰',
        '1': '۱',
        '2': '۲',
        '3': '۳',
        '4': '۴',
        '5': '۵',
        '6': '۶',
        '7': '۷',
        '8': '۸',
        '9': '۹',
    };
    return String(input).replace(/[0-9]/g, (d) => map[d]);
}
const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ end, duration = 1200, prefix = '', suffix = '', className, }) => {
    const [value, setValue] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef<HTMLSpanElement | null>(null);
    const rafRef = useRef<number | null>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !started) {
                    setStarted(true);
                }
            });
        }, { threshold: 0.2 });
        io.observe(el);
        return () => io.disconnect();
    }, [started]);
    useEffect(() => {
        if (!started)
            return;
        const start = performance.now();
        const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(eased * end);
            setValue(current);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current)
                cancelAnimationFrame(rafRef.current);
        };
    }, [duration, end, started]);
    const formatted = `${prefix}${toPersianDigits(value)}${suffix}`;
    return (<span ref={ref} className={cn(className)}>
      {formatted}
    </span>);
};
export default AnimatedCounter;
